import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../../../services/api';

interface Location {
  id: number;
  name: string;
  phone: string | null;
  whatsapp: string | null;
  city: string | null;
  visible: boolean;
  order: number;
}

const emptyForm = { name: '', phone: '', whatsapp: '', city: '', visible: true, order: 0 };

export default function AdminLocationsPage() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editLoc, setEditLoc] = useState<Location | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api.get('/locations/all').then(({ data }) => { setLocations(data.data || data); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditLoc(null); setForm(emptyForm); setShowModal(true); };

  const openEdit = (l: Location) => {
    setEditLoc(l);
    setForm({ name: l.name, phone: l.phone || '', whatsapp: l.whatsapp || '', city: l.city || '', visible: l.visible, order: l.order });
    setShowModal(true);
  };

  const handleSave = async () => {
    const payload: any = { ...form };
    if (!payload.phone) payload.phone = null;
    if (!payload.whatsapp) payload.whatsapp = null;
    if (!payload.city) payload.city = null;
    if (editLoc) { await api.put(`/locations/${editLoc.id}`, payload); }
    else { await api.post('/locations', payload); }
    setShowModal(false);
    load();
  };

  const confirmDelete = async () => {
    if (deleteConfirm) { await api.delete(`/locations/${deleteConfirm}`); setDeleteConfirm(null); load(); }
  };

  const inputClass = "w-full px-3 py-2 text-xs rounded-xl border-2 border-rose-100 bg-rose-50/30 focus:outline-none focus:border-rose-300 transition-all";

  if (loading) return <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div>;

  return (
    <AdminLayout title="מוקדים" subtitle="ניהול מוקדי פעילות">
      <div className="flex justify-end mb-4">
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> הוסף מוקד
        </button>
      </div>

      <div className="bg-white rounded-2xl border-2 border-rose-100 overflow-hidden overflow-x-auto">
        <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_1fr_auto] gap-0 border-b-2 border-rose-100 bg-rose-50/60 px-4 py-3 min-w-[650px]">
          {['שם מוקד', 'טלפון', 'וואטסאפ', 'עיר', 'סטטוס', ''].map((h) => (
            <span key={h} className="text-xs font-bold text-gray-500 px-2">{h}</span>
          ))}
        </div>
        <div className="divide-y-2 divide-rose-50">
          {locations.map((loc) => (
            <div key={loc.id} className="grid grid-cols-[2fr_1.5fr_1.5fr_1fr_1fr_auto] gap-0 px-4 py-3 items-center hover:bg-rose-50/30 transition-colors min-w-[650px]">
              <div className="px-2"><p className="text-xs font-semibold text-gray-800">{loc.name}</p></div>
              <div className="px-2"><p className="text-xs text-gray-600" dir="ltr">{loc.phone || '—'}</p></div>
              <div className="px-2"><p className="text-xs text-gray-600" dir="ltr">{loc.whatsapp || '—'}</p></div>
              <div className="px-2"><p className="text-xs text-gray-600">{loc.city || '—'}</p></div>
              <div className="px-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${loc.visible ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>{loc.visible ? 'פעיל' : 'לא פעיל'}</span>
              </div>
              <div className="px-2 flex gap-1">
                <button onClick={() => openEdit(loc)} className="px-2 py-1 rounded-lg border-2 border-rose-200 bg-rose-50 text-rose-500 text-xs font-semibold hover:bg-rose-100 cursor-pointer whitespace-nowrap">ערוך</button>
                <button onClick={() => setDeleteConfirm(loc.id)} className="px-2 py-1 rounded-lg border-2 border-red-200 bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 cursor-pointer whitespace-nowrap">מחק</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-800 text-base">{editLoc ? 'עריכת מוקד' : 'הוספת מוקד חדש'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer"><i className="ri-close-line text-base"></i></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">שם מוקד *</label><input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">טלפון</label><input type="text" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inputClass} dir="ltr" /></div>
                <div><label className="block text-xs font-semibold text-gray-600 mb-1">וואטסאפ</label><input type="text" value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} className={inputClass} dir="ltr" /></div>
              </div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">עיר</label><input type="text" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className={inputClass} /></div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1">סטטוס</label><div className="flex gap-2">{[['visible', 'פעיל'], ['hidden', 'לא פעיל']].map(([v, l]) => (<button key={v} type="button" onClick={() => setForm((f) => ({ ...f, visible: v === 'visible' }))} className={`flex-1 py-2 rounded-xl border-2 text-xs font-semibold cursor-pointer ${(v === 'visible' && form.visible) || (v === 'hidden' && !form.visible) ? 'bg-rose-400 text-white border-rose-400' : 'bg-white text-gray-400 border-gray-200'}`}>{l}</button>))}</div></div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleSave} className="flex-1 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">{editLoc ? 'שמור שינויים' : 'הוסף מוקד'}</button>
                <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-50 border-2 border-red-100 mx-auto mb-4"><i className="ri-delete-bin-line text-2xl text-red-400"></i></div>
            <h3 className="font-bold text-gray-800 mb-2">מחיקת מוקד</h3>
            <p className="text-sm text-gray-500 mb-5">האם אתה בטוח?</p>
            <div className="flex gap-3">
              <button onClick={confirmDelete} className="flex-1 py-2.5 rounded-xl bg-red-400 text-white text-sm font-bold hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap">מחק</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
